// Phase B (Jul-2026) — Project Monthly Performance P&L.
// Management-only report grouping the SAME P&L sources by "Project
// Reporting Period" (work month) instead of accounting date.
// Tabs: Matrix (month × project) · Trend (per-project months) ·
// Exceptions (reporting ≠ accounting period) · one-click Reconcile
// that ties Σ(all periods) to the frozen CE-R1a Project P&L.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import {
  BadgeCheck, ChevronRight, Download, Eye, EyeOff, Loader2, Play, Search, ShieldAlert, ShieldCheck, X,
} from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";

function currentFy() {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    from_period: `${startYear}-04`,
    to_period: `${(startYear + 1).toString().padStart(4, "0")}-03`,
  };
}

const fmtPeriod = (p) => {
  if (p === "0000-00") return "Opening";
  if (!p || p.length < 7) return p || "—";
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(p.slice(5, 7), 10) - 1]}-${p.slice(0, 4)}`;
};

const SRC_BADGE = {
  explicit: "bg-emerald-50 text-emerald-700 border-emerald-200",
  derived:  "bg-sky-50 text-sky-700 border-sky-200",
  assumed:  "bg-amber-50 text-amber-800 border-amber-300",
};

const MATRIX_COLS = [
  ["revenue_gross", "Revenue"], ["credit_notes", "CN"], ["debit_notes", "DN"],
  ["jv_adjustments", "JV Adj"], ["revenue_net", "Net Revenue"],
  ["material_consumed", "Material"], ["service_cost", "Service/Purchase"],
  ["salary", "Salary"], ["rent", "Rent"], ["general_expenses", "General"],
  ["depreciation", "Dep."], ["total_expense", "Total Expense"],
  ["profit", "Profit/Loss"],
];

const TREND_METRICS = [
  ["revenue_net", "Net Revenue"], ["material_consumed", "Material"],
  ["service_cost", "Service/Purchase"], ["salary", "Salary"], ["rent", "Rent"],
  ["general_expenses", "General"], ["depreciation", "Depreciation"],
  ["total_expense", "Total Expense"], ["profit", "Profit/Loss"],
];

export default function PerformancePnl() {
  const { hasPerm, user } = useAuth();
  const canExport = user?.role === "admin" || hasPerm("performance_pnl", "export");
  const [tab, setTab] = useState("matrix");
  const [filters, setFilters] = useState({ ...currentFy() });
  const [showClosed, setShowClosed] = useState(false);
  const [masters, setMasters] = useState({ projects: [], clients: [], divisions: [] });
  const [data, setData] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [recon, setRecon] = useState(null);
  const [drill, setDrill] = useState(null);       // {period, project_id, project_name}
  const [drillData, setDrillData] = useState(null);
  const [trendProject, setTrendProject] = useState("");
  // Jul-2026 — per-tab quick search boxes
  const [matrixSearch, setMatrixSearch] = useState("");
  const [excSearch, setExcSearch] = useState("");

  useEffect(() => {
    const arr = (r) => (Array.isArray(r.data) ? r.data : r.data?.items || r.data?.rows || []);
    Promise.all([api.get("/projects-picker"), api.get("/clients")])
      .then(([p, c]) => {
        const projects = arr(p);
        setMasters({
          projects,
          clients: arr(c),
          divisions: [...new Set(projects.map((x) => x.division).filter(Boolean))].sort(),
        });
      })
      .catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = {};
    ["project_id", "branch_id", "client_name",
      "division", "project_status"].forEach((k) => { if (filters[k]) p[k] = filters[k]; });
    // Single-month pick overrides the From/To window.
    if (filters.month) {
      p.from_period = filters.month;
      p.to_period = filters.month;
    } else {
      if (filters.from_period) p.from_period = filters.from_period;
      if (filters.to_period) p.to_period = filters.to_period;
    }
    if (showClosed) p.include_closed = true;
    return p;
  }, [filters, showClosed]);

  async function run() {
    setLoading(true);
    setRecon(null);
    try {
      const [m, e] = await Promise.all([
        api.get("/reports/performance-pnl/_matrix", { params }),
        api.get("/reports/performance-pnl/exceptions", { params }),
      ]);
      setData(m.data);
      setExceptions(e.data);
      if (!trendProject && m.data.rows?.length) setTrendProject(m.data.rows[0].project_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
      setData(null); setExceptions(null);
    } finally { setLoading(false); }
  }
  // Mount + whenever the closed-projects toggle flips.
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [showClosed]);

  async function doReconcile() {
    setReconciling(true);
    try {
      const r = await api.get("/reports/performance-pnl/_reconcile",
        { params: { project_id: filters.project_id || undefined, branch_id: filters.branch_id || undefined } });
      setRecon(r.data);
      if (r.data.ok) toast.success(`Reconciled — ${r.data.project_count} project(s) tie out exactly with Project P&L`);
      else toast.error(`${r.data.mismatch_count} project(s) do NOT tie out — see banner`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally { setReconciling(false); }
  }

  async function exportXlsx() {
    setExporting(true);
    try {
      const r = await api.get("/reports/performance-pnl/export.xlsx",
        { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `performance-pnl_${filters.from_period || "all"}_${filters.to_period || "all"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  }

  async function openDrill(row) {
    setDrill({ period: row.period, project_id: row.project_id, project_name: row.project_name });
    setDrillData(null);
    try {
      const r = await api.get("/reports/performance-pnl/drill",
        { params: { project_id: row.project_id, period: row.period } });
      setDrillData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drill failed");
      setDrill(null);
    }
  }

  const rows = useMemo(() => data?.rows || [], [data]);
  // Matrix quick search — filters by project code / name. Period + grand
  // totals are hidden while a search is active (they'd no longer tie out).
  const matrixRows = useMemo(() => {
    const q = matrixSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.project_code} ${r.project_name}`.toLowerCase().includes(q));
  }, [rows, matrixSearch]);
  const periods = useMemo(() => [...new Set(matrixRows.map((r) => r.period))].sort(), [matrixRows]);
  // Distinct projects present in the current window — feeds the Trend
  // tab's searchable picker.
  const trendProjects = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => { if (!seen.has(r.project_id)) seen.set(r.project_id, r); });
    return [...seen.values()].map((r) => ({ id: r.project_id, project_code: r.project_code, project_name: r.project_name }));
  }, [rows]);
  // Exceptions quick search — voucher no / module / project / reason.
  const excRows = useMemo(() => {
    const all = exceptions?.rows || [];
    const q = excSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) =>
      `${r.voucher_number} ${r.module} ${r.project_code} ${r.project_name} ${r.reason}`.toLowerCase().includes(q));
  }, [exceptions, excSearch]);
  const trendRows = useMemo(
    () => rows.filter((r) => r.project_id === trendProject), [rows, trendProject]);
  const trendPeriods = useMemo(
    () => [...new Set(trendRows.map((r) => r.period))].sort(), [trendRows]);
  const trendByPeriod = useMemo(() => {
    const m = {};
    trendRows.forEach((r) => { m[r.period] = r; });
    return m;
  }, [trendRows]);
  const maxAbsProfit = Math.max(1, ...trendPeriods.map((p) => Math.abs(trendByPeriod[p]?.profit || 0)));

  const sel = (k, opts, placeholder, testid) => (
    <select data-testid={testid} className={inputCls} value={filters[k] || ""}
            onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value }))}>
      <option value="">{placeholder}</option>
      {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  return (
    <div className="space-y-4" data-testid="performance-pnl-page">
      <Toaster position="top-right" richColors />
      <PageHeader
        title="Project Monthly Performance P&L"
        subtitle="Management view by Project Reporting Period (work month) — accounting, GST and the standard Project P&L stay on voucher dates"
      />

      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Single Month</div>
            <input type="month" data-testid="ppnl-single-month" className={inputCls}
                   value={filters.month || ""}
                   onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))}/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">From Month</div>
            <input type="month" data-testid="ppnl-from-period" className={`${inputCls} ${filters.month ? "opacity-40" : ""}`}
                   disabled={!!filters.month}
                   title={filters.month ? "Clear Single Month to use a range" : ""}
                   value={filters.from_period || ""}
                   onChange={(e) => setFilters((f) => ({ ...f, from_period: e.target.value }))}/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">To Month</div>
            <input type="month" data-testid="ppnl-to-period" className={`${inputCls} ${filters.month ? "opacity-40" : ""}`}
                   disabled={!!filters.month}
                   title={filters.month ? "Clear Single Month to use a range" : ""}
                   value={filters.to_period || ""}
                   onChange={(e) => setFilters((f) => ({ ...f, to_period: e.target.value }))}/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Project</div>
            <SearchableSelect
              testid="ppnl-filter-project"
              className="w-60"
              value={filters.project_id || ""}
              onChange={(id) => setFilters((f) => ({ ...f, project_id: id }))}
              items={masters.projects}
              getKey={(p) => p.id}
              getLabel={(p) => `${p.project_code} — ${p.project_name}`}
              placeholder="All projects"
              searchPlaceholder="Search project code / name…"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Client</div>
            <SearchableSelect
              testid="ppnl-filter-client"
              className="w-48"
              value={filters.client_name || ""}
              onChange={(name) => setFilters((f) => ({ ...f, client_name: name }))}
              items={masters.clients}
              getKey={(c) => c.name}
              getLabel={(c) => c.name}
              placeholder="All clients"
              searchPlaceholder="Search client…"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Branch</div>
            <BranchFilter value={filters.branch_id || ""}
                          onChange={(v) => setFilters((f) => ({ ...f, branch_id: v }))}/>
          </div>
          {masters.divisions.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Division</div>
              {sel("division", masters.divisions.map((d) => ({ value: d, label: d })),
                "All divisions", "ppnl-filter-division")}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Status</div>
            {sel("project_status",
              ["Active", "Completed", "Hold"].map((s) => ({ value: s, label: s })),
              "All statuses", "ppnl-filter-status")}
          </div>
          <button onClick={() => setShowClosed((v) => !v)} data-testid="ppnl-toggle-closed"
                  title="Closed (Completed) projects are hidden by default"
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 border font-bold ${showClosed
                    ? "bg-slate-700 text-white border-slate-800 hover:bg-slate-800"
                    : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
            {showClosed ? <Eye size={12}/> : <EyeOff size={12}/>}
            {showClosed ? "Closed: Shown" : "Show Closed"}
          </button>
          <button onClick={run} disabled={loading} data-testid="ppnl-run"
                  className="inline-flex items-center gap-1.5 text-xs px-4 py-2 bg-orange-600 text-white border border-orange-700 hover:bg-orange-700 disabled:opacity-60 font-bold">
            {loading ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>} Run
          </button>
          <button onClick={doReconcile} disabled={reconciling} data-testid="ppnl-reconcile"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-60">
            {reconciling ? <Loader2 size={12} className="animate-spin"/> : <BadgeCheck size={12}/>} Verify vs Project P&L
          </button>
          {canExport && (
            <button onClick={exportXlsx} disabled={exporting || !rows.length} data-testid="ppnl-export"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-60">
              {exporting ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} Excel
            </button>
          )}
        </div>
      </Card>

      {recon && (
        <div data-testid="ppnl-recon-banner"
             className={`flex items-start gap-2 text-xs p-3 border ${recon.ok
               ? "bg-emerald-50 border-emerald-300 text-emerald-900"
               : "bg-rose-50 border-rose-300 text-rose-900"}`}>
          {recon.ok ? <ShieldCheck size={14} className="mt-0.5 shrink-0"/> : <ShieldAlert size={14} className="mt-0.5 shrink-0"/>}
          <div>
            {recon.ok
              ? <><b>Integrity verified.</b> Σ of all reporting periods equals the standard Project P&L exactly for all {recon.project_count} project(s). Only the month placement differs — totals never change.</>
              : <><b>{recon.mismatch_count} project(s) do not tie out.</b> {recon.projects.filter((p) => !p.ok).slice(0, 5).map((p) => p.project_code).join(", ")} — please report this to the administrator.</>}
          </div>
        </div>
      )}

      {data && data.unresolved_count > 0 && (
        <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 p-2.5"
             data-testid="ppnl-assumed-banner">
          <b>{data.unresolved_count}</b> contribution(s) had no reporting period and are shown in their <b>accounting month</b> (basis “assumed”). They can be refined later without affecting accounting.
        </div>
      )}

      <div className="flex gap-1 border-b border-slate-200">
        {[["matrix", "Monthly Matrix"], ["trend", "Project Trend"],
          ["exceptions", `Exceptions${exceptions ? ` (${exceptions.count})` : ""}`]].map(([k, label]) => (
          <button key={k} data-testid={`ppnl-tab-${k}`} onClick={() => setTab(k)}
                  className={`text-xs px-4 py-2 border-b-2 font-bold uppercase tracking-wide ${tab === k
                    ? "border-orange-600 text-orange-700"
                    : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center py-10 text-slate-500 text-sm flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin"/> Building performance matrix…
        </div>
      )}

      {!loading && tab === "matrix" && data && (
        <Card className="p-0 overflow-x-auto" data-testid="ppnl-matrix-table">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 sticky left-0">
            <Search size={13} className="text-slate-400 shrink-0" />
            <input
              data-testid="ppnl-matrix-search"
              value={matrixSearch}
              onChange={(e) => setMatrixSearch(e.target.value)}
              placeholder="Search project code / name…"
              className="text-xs flex-1 py-1 focus:outline-none bg-transparent"
            />
            {matrixSearch && (
              <>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{matrixRows.length} row(s) · totals hidden while searching</span>
                <button data-testid="ppnl-matrix-search-clear" onClick={() => setMatrixSearch("")}
                        className="text-slate-400 hover:text-slate-700"><X size={12}/></button>
              </>
            )}
          </div>
          {/* Jul-2026 — fixed-height scroll box: the matrix scrolls inside
              the card instead of stretching the page. Header stays sticky. */}
          <div className="max-h-[65vh] overflow-auto">
          <table className="text-xs w-full">
            <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
              <tr>
                <th className="text-left py-2 px-2 whitespace-nowrap">Period</th>
                <th className="text-left py-2 px-2 whitespace-nowrap">Project</th>
                {MATRIX_COLS.map(([k, h]) => (
                  <th key={k} className="text-right py-2 px-2 whitespace-nowrap">{h}</th>
                ))}
                <th className="text-right py-2 px-2 whitespace-nowrap">Margin %</th>
                <th className="text-left py-2 px-2 whitespace-nowrap">Basis</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {periods.map((period) => {
                const prow = matrixRows.filter((r) => r.period === period);
                const ptot = (data.period_totals || []).find((t) => t.period === period);
                return [
                  ...prow.map((r, i) => (
                    <tr key={`${period}-${r.project_id}`}
                        data-testid={`ppnl-row-${period}-${i}`}
                        onClick={() => openDrill(r)}
                        className="border-t border-slate-100 hover:bg-orange-50/40 cursor-pointer">
                      <td className="py-1.5 px-2 font-mono text-[11px]">{i === 0 ? fmtPeriod(period) : ""}</td>
                      <td className="py-1.5 px-2">
                        <div className="font-semibold text-slate-800">{r.project_code}
                          {r.project_status === "Completed" && (
                            <span className="ml-1.5 inline-block px-1 py-0.5 border border-slate-300 bg-slate-100 text-slate-500 text-[9px] uppercase tracking-wider align-middle">Closed</span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-500">{r.project_name}</div>
                      </td>
                      {MATRIX_COLS.map(([k]) => (
                        <td key={k} className={`py-1.5 px-2 text-right tabular-nums ${k === "profit"
                          ? (r.profit >= 0 ? "text-emerald-700 font-bold" : "text-rose-700 font-bold") : ""}`}>
                          {r[k] ? INR(r[k]) : "—"}
                        </td>
                      ))}
                      <td className="py-1.5 px-2 text-right tabular-nums">
                        {r.margin_pct === null ? "—" : `${r.margin_pct}%`}
                      </td>
                      <td className="py-1.5 px-2 whitespace-nowrap">
                        {r.assumed_count > 0 && (
                          <span className={`inline-block px-1.5 py-0.5 border text-[10px] ${SRC_BADGE.assumed}`}>
                            {r.assumed_count} assumed
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 px-1 text-slate-400"><ChevronRight size={12}/></td>
                    </tr>
                  )),
                  !matrixSearch && ptot && prow.length > 1 && (
                    <tr key={`${period}-total`} className="border-t border-slate-200 bg-slate-50 font-bold">
                      <td className="py-1.5 px-2"/>
                      <td className="py-1.5 px-2 text-slate-600">{fmtPeriod(period)} total · {ptot.project_count} project(s)</td>
                      {MATRIX_COLS.map(([k]) => (
                        <td key={k} className="py-1.5 px-2 text-right tabular-nums">{ptot[k] ? INR(ptot[k]) : "—"}</td>
                      ))}
                      <td className="py-1.5 px-2 text-right tabular-nums">{ptot.margin_pct === null ? "—" : `${ptot.margin_pct}%`}</td>
                      <td/><td/>
                    </tr>
                  ),
                ];
              })}
              {!matrixSearch && data.totals && (
                <tr className="border-t-2 border-slate-300 bg-orange-50/60 font-bold" data-testid="ppnl-grand-total">
                  <td className="py-2 px-2" colSpan={2}>GRAND TOTAL · {data.totals.project_count} project(s) · {data.totals.period_count} month(s)</td>
                  {MATRIX_COLS.map(([k]) => (
                    <td key={k} className="py-2 px-2 text-right tabular-nums">{data.totals[k] ? INR(data.totals[k]) : "—"}</td>
                  ))}
                  <td className="py-2 px-2 text-right tabular-nums">{data.totals.margin_pct === null ? "—" : `${data.totals.margin_pct}%`}</td>
                  <td/><td/>
                </tr>
              )}
              {matrixRows.length === 0 && (
                <tr><td colSpan={MATRIX_COLS.length + 5} className="text-center py-8 text-slate-400">
                  {matrixSearch ? "No projects match your search." : "No P&L activity in this window."}</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {!loading && tab === "trend" && (
        <Card className="p-3 space-y-3" data-testid="ppnl-trend">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">Project</div>
            <SearchableSelect
              testid="ppnl-trend-project"
              value={trendProject}
              onChange={(id) => setTrendProject(id)}
              items={trendProjects}
              getKey={(p) => p.id}
              getLabel={(p) => `${p.project_code} — ${p.project_name}`}
              getSearchText={(p) => `${p.project_code} ${p.project_name}`}
              placeholder="Select project…"
              searchPlaceholder="Type to search project code / name…"
              allowClear={false}
              className="w-96 max-w-full"
            />
          </div>
          {trendPeriods.length === 0
            ? <div className="text-center py-8 text-slate-400 text-sm">No activity for this project in the selected window.</div>
            : (
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead className="bg-slate-100 text-slate-700">
                    <tr>
                      <th className="text-left py-2 px-2">Metric</th>
                      {trendPeriods.map((p) => <th key={p} className="text-right py-2 px-2 whitespace-nowrap">{fmtPeriod(p)}</th>)}
                      <th className="text-right py-2 px-2 bg-orange-50">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TREND_METRICS.map(([k, label]) => {
                      const total = trendPeriods.reduce((s, p) => s + (trendByPeriod[p]?.[k] || 0), 0);
                      const isProfit = k === "profit";
                      return (
                        <tr key={k} className={`border-t border-slate-100 ${isProfit ? "font-bold" : ""}`}>
                          <td className="py-1.5 px-2">{label}</td>
                          {trendPeriods.map((p) => {
                            const v = trendByPeriod[p]?.[k] || 0;
                            return (
                              <td key={p} className={`py-1.5 px-2 text-right tabular-nums ${isProfit
                                ? (v >= 0 ? "text-emerald-700" : "text-rose-700") : ""}`}>
                                {v ? INR(v) : "—"}
                              </td>
                            );
                          })}
                          <td className={`py-1.5 px-2 text-right tabular-nums bg-orange-50/50 font-bold ${isProfit
                            ? (total >= 0 ? "text-emerald-700" : "text-rose-700") : ""}`}>{INR(total)}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-slate-100">
                      <td className="py-1.5 px-2">Margin %</td>
                      {trendPeriods.map((p) => {
                        const r = trendByPeriod[p];
                        return <td key={p} className="py-1.5 px-2 text-right tabular-nums">
                          {r?.margin_pct === null || r?.margin_pct === undefined ? "—" : `${r.margin_pct}%`}</td>;
                      })}
                      <td className="py-1.5 px-2 bg-orange-50/50"/>
                    </tr>
                    <tr className="border-t border-slate-200">
                      <td className="py-2 px-2 text-slate-500 text-[10px] uppercase font-bold">Profit trend</td>
                      {trendPeriods.map((p) => {
                        const v = trendByPeriod[p]?.profit || 0;
                        const h = Math.max(4, Math.round(Math.abs(v) / maxAbsProfit * 44));
                        return (
                          <td key={p} className="py-2 px-2 align-bottom">
                            <div className="flex justify-end">
                              <div title={INR(v)}
                                   className={v >= 0 ? "bg-emerald-500" : "bg-rose-500"}
                                   style={{ width: 18, height: h }}/>
                            </div>
                          </td>
                        );
                      })}
                      <td className="bg-orange-50/50"/>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
        </Card>
      )}

      {!loading && tab === "exceptions" && exceptions && (
        <Card className="p-0 overflow-x-auto" data-testid="ppnl-exceptions-table">
          <div className="flex items-center justify-between gap-3 px-3 py-2 text-[11px] text-slate-500 border-b border-slate-200">
            <div>
              Vouchers whose <b>Project Reporting Period</b> differs from the <b>Accounting Period</b> — for Finance review.
              {exceptions.count > 0 && <> Total amount involved: <b className="text-slate-800">{INR(exceptions.total_amount)}</b></>}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Search size={13} className="text-slate-400" />
              <input
                data-testid="ppnl-exceptions-search"
                value={excSearch}
                onChange={(e) => setExcSearch(e.target.value)}
                placeholder="Search voucher / project / reason…"
                className="text-xs py-1 w-60 focus:outline-none bg-transparent border-b border-slate-200 focus:border-orange-400"
              />
              {excSearch && (
                <button data-testid="ppnl-exceptions-search-clear" onClick={() => setExcSearch("")}
                        className="text-slate-400 hover:text-slate-700"><X size={12}/></button>
              )}
            </div>
          </div>
          {/* Jul-2026 — fixed-height scroll box, sticky header (same as Matrix). */}
          <div className="max-h-[65vh] overflow-auto">
          <table className="text-xs w-full">
            <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
              <tr>
                <th className="text-left py-2 px-2">Voucher #</th>
                <th className="text-left py-2 px-2">Module</th>
                <th className="text-left py-2 px-2">Project</th>
                <th className="text-left py-2 px-2 whitespace-nowrap">Accounting Period</th>
                <th className="text-left py-2 px-2 whitespace-nowrap">Reporting Period</th>
                <th className="text-right py-2 px-2 whitespace-nowrap">Shift (months)</th>
                <th className="text-left py-2 px-2">Basis / Reason</th>
                <th className="text-right py-2 px-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {excRows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`ppnl-exception-${i}`}>
                  <td className="py-1.5 px-2 font-mono text-[11px]">{r.voucher_number}</td>
                  <td className="py-1.5 px-2">{r.module}</td>
                  <td className="py-1.5 px-2">
                    <div className="font-semibold">{r.project_code}</div>
                    <div className="text-[10px] text-slate-500">{r.project_name}</div>
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[11px]">{fmtPeriod(r.accounting_period)}</td>
                  <td className="py-1.5 px-2 font-mono text-[11px] font-bold">{fmtPeriod(r.reporting_period)}</td>
                  <td className={`py-1.5 px-2 text-right tabular-nums ${r.shift_months < 0 ? "text-sky-700" : "text-amber-700"}`}>
                    {r.shift_months > 0 ? `+${r.shift_months}` : r.shift_months}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={`inline-block px-1.5 py-0.5 border text-[10px] mr-1.5 ${SRC_BADGE[r.pm_source] || SRC_BADGE.assumed}`}>
                      {r.pm_source}
                    </span>
                    <span className="text-slate-600">{r.reason}</span>
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{INR(r.amount)}</td>
                </tr>
              ))}
              {excRows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">
                  {excSearch ? "No exceptions match your search." : "No exceptions — every voucher's reporting period matches its accounting period."}</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {drill && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-10"
             data-testid="ppnl-drill-dialog" role="dialog" onClick={() => setDrill(null)}>
          <div className="bg-white shadow-2xl w-full max-w-5xl mx-4 border border-slate-300"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-bold text-slate-800">{drill.project_name}</div>
                <div className="text-[11px] text-slate-500">Reporting Period {fmtPeriod(drill.period)} — voucher-level contributions</div>
              </div>
              <button data-testid="ppnl-drill-close" onClick={() => setDrill(null)}
                      className="text-slate-500 hover:text-slate-800"><X size={16}/></button>
            </div>
            <div className="p-3 max-h-[65vh] overflow-auto">
              {!drillData
                ? <div className="text-center py-8 text-slate-500 text-sm flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin"/> Loading…</div>
                : (
                  <table className="text-xs w-full">
                    <thead className="bg-slate-100 text-slate-700 sticky top-0">
                      <tr>
                        <th className="text-left py-1.5 px-2">Source</th>
                        <th className="text-left py-1.5 px-2">Voucher #</th>
                        <th className="text-left py-1.5 px-2 whitespace-nowrap">Accounting Date</th>
                        <th className="text-left py-1.5 px-2">Basis</th>
                        <th className="text-left py-1.5 px-2">Reason</th>
                        <th className="text-right py-1.5 px-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillData.rows.map((c, i) => (
                        <tr key={i} className="border-t border-slate-100" data-testid={`ppnl-drill-row-${i}`}>
                          <td className="py-1.5 px-2">{c.source_label}</td>
                          <td className="py-1.5 px-2 font-mono text-[11px]">{c.voucher_number}</td>
                          <td className="py-1.5 px-2 font-mono text-[11px]">{c.accounting_date}</td>
                          <td className="py-1.5 px-2">
                            <span className={`inline-block px-1.5 py-0.5 border text-[10px] ${SRC_BADGE[c.pm_source] || SRC_BADGE.assumed}`}>
                              {c.pm_source}
                            </span>
                          </td>
                          <td className="py-1.5 px-2 text-slate-600">{c.reason}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums">{INR(c.amount)}</td>
                        </tr>
                      ))}
                      {drillData.rows.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-6 text-slate-400">No contributions.</td></tr>
                      )}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
